import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Routes, Route, useNavigate, useParams } from 'react-router-dom'
import {
  Container,
  Row,
  Col,
  Table,
  Alert,
  Button,
  Form,
  Card,
  Modal
} from 'react-bootstrap'
import { generateReceipt, salesList } from '../actions/posActions.js'
import { updateInventorymulti } from '../actions/inventoryActions.js'
import SearchPos from '../components/PosSearchBox.js'
import PaginatePos from '../components/PaginatePos.js'
import Message from '../components/Message.js'
import Loader from '../components/Loader.js'
import AddShipping from '../components/AddShipping.js'
import { ThemeContext } from '../components/ThemeContext.js'
import { MULTI_INVENTORY_UPDATE_RESET } from '../constants/inventoryConstants.js'
import { GET_SALE_ITEM_RESET } from '../constants/saleConstants.js'
import { trackEvent, toAnalyticsItem } from '../utils/analytics.js'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons'

const defaultItemInput = {
  quantity: 1,
  discount: 0
}

const ANALYTICS_CURRENCY = 'USD'

const Pointofsale = () => {
  const { theme } = useContext(ThemeContext)
  const [show, setShow] = useState(false)
  const [shipping, setShipping] = useState(0)
  const [qrid, setQrid] = useState('')
  const [cartItems, setCartItems] = useState([])
  const [itemInputs, setItemInputs] = useState({})
  const [notice, setNotice] = useState(null)
  const [pendingPurchase, setPendingPurchase] = useState(null)

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const handleClose = () => setShow(false)

  const { pageNumberpos = 1, keyword = '' } = useParams()

  const userLogin = useSelector((state) => state.userLogin)
  const { userInfo } = userLogin

  const ownerLogin = useSelector((state) => state.ownerLogin)
  const { ownerInfo } = ownerLogin

  const activeSession = ownerInfo || userInfo
  const bname = activeSession?.bname || activeSession?.name || ''
  const vfname = bname.toLowerCase().replace(/\s/g, '')

  const saleList = useSelector((state) => state.saleList)
  const { sales = [], loading, error, pagespos, pagepos, countpos } = saleList

  const settingList = useSelector((state) => state.settingList)
  const { settings = {} } = settingList

  const multiInventoryUpdate = useSelector(
    (state) => state.multiInventoryUpdate || {}
  )
  const { success: inventoryUpdateSuccess, error: inventoryUpdateError } =
    multiInventoryUpdate

  const taxrate = Number(settings?.taxrate || 0)
  const totalwithtax = 1 + taxrate / 100

  const currentsub = useMemo(
    () => cartItems.reduce((total, item) => total + item.lineSubtotal, 0),
    [cartItems]
  )

  const totaldisc = useMemo(
    () => cartItems.reduce((total, item) => total + item.lineDiscount, 0),
    [cartItems]
  )

  const allmoney = useMemo(
    () => ({
      total: ((currentsub + shipping) * totalwithtax).toFixed(2),
      subtotal: currentsub.toFixed(2),
      discount: totaldisc.toFixed(2),
      shipping: shipping.toFixed(2),
      taxes: (((currentsub + shipping) * taxrate) / 100).toFixed(2)
    }),
    [currentsub, shipping, totaldisc, taxrate, totalwithtax]
  )

  const inventoryupdate = useMemo(
    () =>
      cartItems.map((saleitem) => ({
        _id: saleitem.id,
        qty: saleitem.allqty - saleitem.quantity,
        cid: saleitem.cid,
        upc: saleitem.upc,
        saleqty: saleitem.quantity,
        item: saleitem.item,
        manufacturer: saleitem.manufacturer,
        subname: saleitem.subname,
        price: Number(saleitem.price).toFixed(2),
        itemdiscount: saleitem.itemdiscount
      })),
    [cartItems]
  )

  const objcount = inventoryupdate.length

  const handleShow = () => {
    if (cartItems.length > 0) {
      trackEvent('begin_checkout', {
        currency: ANALYTICS_CURRENCY,
        value: Number(allmoney.total),
        items: cartItems.map(toAnalyticsItem)
      })
    }

    setShow(true)
  }

  const childToparent = (data) => {
    const nextShipping = Math.max(0, Number(data) || 0)
    setShipping(nextShipping)
  }

  const handleItemInputChange = (id, field, value, maxQty = Infinity) => {
    const numericValue = Number(value)
    const safeValue = Number.isFinite(numericValue) ? numericValue : 0

    setItemInputs((prev) => ({
      ...prev,
      [id]: {
        ...(prev[id] || defaultItemInput),
        [field]:
          field === 'quantity' ?
            Math.max(1, Math.min(maxQty, Math.trunc(safeValue || 1)))
          : Math.max(0, Math.min(50, safeValue))
      }
    }))
  }

  const deleteItem = (id) => {
    const removedItem = cartItems.find((item) => item.id === id)

    if (removedItem) {
      trackEvent('remove_from_cart', {
        currency: ANALYTICS_CURRENCY,
        value: removedItem.lineSubtotal,
        items: [toAnalyticsItem(removedItem)]
      })
    }

    setCartItems((prev) => prev.filter((item) => item.id !== id))
    setNotice(null)
  }

  const addToReceipt = (unit) => {
    const currentInput = itemInputs[unit._id] || defaultItemInput
    const nextQuantity = Math.max(
      1,
      Math.min(unit.qty, Math.trunc(Number(currentInput.quantity) || 1))
    )
    const nextDiscount = Math.max(
      0,
      Math.min(50, Number(currentInput.discount) || 0)
    )

    if (cartItems.some((item) => item.id === unit._id)) {
      setNotice({
        variant: 'warning',
        message: 'Item already in receipt.'
      })
      return
    }

    const appliedDiscount = 1 - nextDiscount / 100
    const discprice = Number((unit.price * appliedDiscount).toFixed(2))
    const lineSubtotal = Number((discprice * nextQuantity).toFixed(2))
    const lineDiscount = Number(
      (unit.price * nextQuantity - lineSubtotal).toFixed(2)
    )

    const cartItem = {
      id: unit._id,
      upc: unit.upc,
      cid: unit.cid,
      item: unit.item,
      manufacturer: unit.manufacturer,
      subname: unit.subname,
      price: Number(unit.price),
      allqty: unit.qty,
      quantity: nextQuantity,
      itemdiscount: nextDiscount,
      discprice,
      lineSubtotal,
      lineDiscount
    }

    setCartItems((prev) => [...prev, cartItem])

    trackEvent('add_to_cart', {
      currency: ANALYTICS_CURRENCY,
      value: lineSubtotal,
      items: [toAnalyticsItem(cartItem)]
    })

    setItemInputs((prev) => ({
      ...prev,
      [unit._id]: defaultItemInput
    }))

    setNotice(null)
  }

  const generateRandomNumber = () => {
    const min = 100000000000
    const max = 999999999999

    return Math.floor(Math.random() * (max - min + 1)) + min
  }

  const resetReceiptState = () => {
    setCartItems([])
    setItemInputs({})
    setShipping(0)
    setQrid('')
    setNotice(null)
  }

  const cancelRcpt = () => {
    if (cartItems.length === 0) {
      return
    }

    resetReceiptState()
    handleClose()
    navigate('/pos')
  }

  const checkOut = (e) => {
    e.preventDefault()

    if (inventoryupdate.length === 0) {
      setNotice({
        variant: 'warning',
        message: 'Add at least one item before checkout.'
      })
      handleClose()
      return
    }

    const rstoredate = new Date()
    const date = `0${rstoredate.getDate()}`.slice(-2)
    const month = `0${rstoredate.getMonth() + 1}`.slice(-2)
    const year = rstoredate.getFullYear()
    const hours = `0${rstoredate.getHours()}`.slice(-2)
    const minutes = `0${rstoredate.getMinutes()}`.slice(-2)
    const barcode = generateRandomNumber().toString()
    const qfname = `${date}${month}${year}${hours}${minutes}-${barcode}-${vfname || 'receipt'}.png`
    const receiptPayload = [allmoney, ...inventoryupdate]

    setPendingPurchase({
      transactionId: barcode,
      affiliation: bname || undefined,
      value: Number(allmoney.total),
      tax: Number(allmoney.taxes),
      shipping: Number(allmoney.shipping),
      items: cartItems.map(toAnalyticsItem)
    })

    dispatch(updateInventorymulti(inventoryupdate, objcount))

    if (qrid.trim() !== '') {
      dispatch(
        generateReceipt(receiptPayload, {
          qrid: qrid.trim(),
          qfname,
          barcode
        })
      )
    }

    handleClose()
    resetReceiptState()
    navigate('/pos')
  }

  useEffect(() => {
    dispatch(salesList(pageNumberpos, keyword))
  }, [dispatch, pageNumberpos, keyword])

  useEffect(() => {
    if (!pendingPurchase || !inventoryUpdateSuccess) {
      return
    }

    trackEvent('purchase', {
      transaction_id: pendingPurchase.transactionId,
      affiliation: pendingPurchase.affiliation,
      currency: ANALYTICS_CURRENCY,
      value: pendingPurchase.value,
      tax: pendingPurchase.tax,
      shipping: pendingPurchase.shipping,
      items: pendingPurchase.items
    })

    setPendingPurchase(null)
    dispatch({ type: MULTI_INVENTORY_UPDATE_RESET })
  }, [dispatch, inventoryUpdateSuccess, pendingPurchase])

  useEffect(() => {
    if (!pendingPurchase || !inventoryUpdateError) {
      return
    }

    setPendingPurchase(null)
    dispatch({ type: MULTI_INVENTORY_UPDATE_RESET })
  }, [dispatch, inventoryUpdateError, pendingPurchase])

  useEffect(() => {
    if (!activeSession) {
      dispatch({ type: GET_SALE_ITEM_RESET })
      navigate('/login')
    }
  }, [dispatch, navigate, activeSession])

  return (
    <Container style={{ color: theme.text }}>
      <Row className='align-items-center my-2'>
        <Col>
          <h3 className='my-2' style={{ color: theme.text }}>
            Point of Sale Terminal
          </h3>
        </Col>
      </Row>
      <Row className='align-items-center my-2'>
        <Col>
          <Routes>
            <Route path='/*' element={<SearchPos />} />
          </Routes>
        </Col>
      </Row>

      <PaginatePos pagespos={pagespos} pagepos={pagepos} />
      {notice && (
        <Alert
          variant={notice.variant}
          dismissible
          onClose={() => setNotice(null)}
        >
          {notice.message}
        </Alert>
      )}
      {countpos === 0 && <Message variant='info'>None Found</Message>}
      {loading ?
        <Loader />
      : error ?
        <Message variant='danger'>{error}</Message>
      : <Row className='g-3 align-items-start'>
          <Col xs={12} lg={7} style={{ minWidth: 0 }}>
            <Table
              striped
              bordered
              hover
              responsive='lg'
              className='table-sm my-2'
              variant='active'
            >
              <thead>
                <tr>
                  <th>Mfg</th>
                  <th>Name</th>
                  <th>Qty</th>
                  <th>Price</th>
                  <th width='190px'>Select</th>
                  <th width='110px'>Disc %</th>
                </tr>
              </thead>
              <tbody>
                {sales.map(
                  (unit) =>
                    unit.qty > 0 && (
                      <tr key={unit._id}>
                        <td>{unit.manufacturer}</td>
                        <td>{unit.subname}</td>
                        <td>{unit.qty}</td>
                        <td align='right'>
                          {unit.price.toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD'
                          })}
                        </td>
                        <td>
                          <Form className='d-flex align-items-center gap-2 flex-nowrap'>
                            <Form.Group
                              className='mb-0'
                              controlId={`quantity-${unit._id}`}
                            >
                              <Form.Control
                                style={{ minWidth: '72px' }}
                                type='number'
                                min='1'
                                max={unit.qty}
                                value={itemInputs[unit._id]?.quantity ?? 1}
                                onChange={(e) =>
                                  handleItemInputChange(
                                    unit._id,
                                    'quantity',
                                    e.target.value,
                                    unit.qty
                                  )
                                }
                              ></Form.Control>
                            </Form.Group>
                            <Button
                              type='button'
                              variant='primary'
                              size='sm'
                              className='d-flex align-items-center justify-content-center gap-1'
                              onClick={() => addToReceipt(unit)}
                            >
                              <FontAwesomeIcon icon={faPlus} /> Add
                            </Button>
                          </Form>
                        </td>
                        <td>
                          <Form.Group
                            className='mb-0'
                            controlId={`discount-${unit._id}`}
                          >
                            <Form.Control
                              style={{ minWidth: '72px' }}
                              type='number'
                              min='0'
                              max='50'
                              value={itemInputs[unit._id]?.discount ?? 0}
                              onChange={(e) =>
                                handleItemInputChange(
                                  unit._id,
                                  'discount',
                                  e.target.value
                                )
                              }
                            ></Form.Control>
                          </Form.Group>
                        </td>
                      </tr>
                    )
                )}
              </tbody>
            </Table>
          </Col>
          <Col xs={12} lg={5} style={{ minWidth: 0 }}>
            <Card
              className='h-100'
              style={{
                backgroundColor: theme.card,
                color: theme.text,
                borderColor: theme.sidebarAccent
              }}
            >
              <Card.Header
                style={{
                  backgroundColor: theme.surfaceMuted,
                  borderColor: theme.sidebarAccent
                }}
              >
                <h4 style={{ color: theme.text }}>Receipt</h4>
              </Card.Header>
              <Card.Body>
                <Table
                  striped
                  bordered
                  hover
                  responsive='md'
                  className='mb-0 table-sm pos-receipt-table'
                >
                  <colgroup>
                    <col style={{ width: '18%' }} />
                    <col style={{ width: '18%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '16%' }} />
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '18%' }} />
                    <col style={{ width: '8%' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Mfg</th>
                      <th>Name</th>
                      <th>Qty</th>
                      <th>Per</th>
                      <th>Disc</th>
                      <th>Price</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {cartItems.length > 0 ?
                      cartItems.map((saleitem) => (
                        <tr key={saleitem.id}>
                          <td>{saleitem.manufacturer}</td>
                          <td>{saleitem.subname}</td>
                          <td align='center'>{saleitem.quantity}</td>
                          <td align='right'>
                            {saleitem.price.toLocaleString('en-US', {
                              style: 'currency',
                              currency: 'USD'
                            })}
                          </td>
                          <td align='right'>{saleitem.itemdiscount}%</td>
                          <td align='right'>
                            {saleitem.lineSubtotal.toLocaleString('en-US', {
                              style: 'currency',
                              currency: 'USD'
                            })}
                          </td>
                          <td>
                            <Button
                              type='button'
                              variant='danger'
                              size='sm'
                              onClick={() => deleteItem(saleitem.id)}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </Button>
                          </td>
                        </tr>
                      ))
                    : <tr>
                        <td colSpan='7' className='text-center'>
                          Add items to begin a sale.
                        </td>
                      </tr>
                    }
                  </tbody>
                </Table>
              </Card.Body>
              <Card.Footer>
                <Row>
                  <Col>Subtotal</Col>
                  <Col align='right'>
                    {currentsub.toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD'
                    })}
                  </Col>
                </Row>
                <Row>
                  <Col>Disc on sale</Col>
                  <Col align='right'>
                    {'-'}{' '}
                    {totaldisc.toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD'
                    })}
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <AddShipping childToparent={childToparent} />
                    <hr className='solid' />
                  </Col>
                  <Col align='right'>
                    {shipping.toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD'
                    })}
                  </Col>
                </Row>
                <Row>
                  <Col>Tax</Col>
                  <Col align='right'>
                    {(((currentsub + shipping) * taxrate) / 100).toLocaleString(
                      'en-US',
                      {
                        style: 'currency',
                        currency: 'USD'
                      }
                    )}{' '}
                    @ {taxrate}%
                  </Col>
                </Row>
                <Row>
                  <Col>Total</Col>
                  <Col align='right'>
                    {((currentsub + shipping) * totalwithtax).toLocaleString(
                      'en-US',
                      {
                        style: 'currency',
                        currency: 'USD'
                      }
                    )}
                    <br />
                  </Col>
                </Row>
                <Col>
                  {objcount > 0 && (
                    <Button
                      className='btn btn-outline btn-sm text-left'
                      size='sm'
                      onClick={handleShow}
                    >
                      Checkout
                    </Button>
                  )}
                  <Modal show={show} onHide={handleClose}>
                    <Modal.Header closeButton>
                      <Modal.Title>Finish Sale</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                      <Col>
                        Total:{' '}
                        {(
                          (currentsub + shipping) *
                          totalwithtax
                        ).toLocaleString('en-US', {
                          style: 'currency',
                          currency: 'USD'
                        })}
                      </Col>

                      <Form.Group controlId='qrid'>
                        <Form.Control
                          autoFocus
                          type='text'
                          align='left'
                          width='80%'
                          placeholder='Scan QRcode option'
                          value={qrid}
                          onChange={(e) => setQrid(e.target.value)}
                        ></Form.Control>
                      </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                      <Button variant='danger' onClick={handleClose}>
                        Cancel
                      </Button>
                      <Button variant='primary' onClick={checkOut}>
                        Save
                      </Button>
                    </Modal.Footer>
                  </Modal>
                </Col>
                <Col align='right'>
                  <Button
                    type='button'
                    variant='danger'
                    size='sm'
                    align='right'
                    onClick={cancelRcpt}
                  >
                    Cancel Sale
                  </Button>
                </Col>
              </Card.Footer>
            </Card>
          </Col>
        </Row>
      }
    </Container>
  )
}

export default Pointofsale
